import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Icon, Input, Label } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';

interface AcceptInvitePageProps {
  siteName: string;
}

// Step 2 of the reset/invite flow — token comes in via URL query and the
// user picks a new password. Used by both `?token=...` invite emails and
// the password-reset links.
export function AcceptInvitePage({ siteName }: AcceptInvitePageProps) {
  const displayName = siteName?.trim() || 'Access Portal';
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Both fields are client-side typed by the same user — no secret comparison,
    // so the timing-attack rule is a false positive here.
    // eslint-disable-next-line security/detect-possible-timing-attacks
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (!token) {
      setError('Missing or expired reset token. Request a new link.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // Backend expects `new_password` (not `password`) per
      // packages/domain/src/schemas/auth.ts.
      await apiClient.post('/api/v1/auth/reset', { token, new_password: password });
      navigate('/sign-in', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  }

  // GAP-AF3 (audit 2026-05-21): hitting /accept-invite without a ?token=
  // used to render the password-set form anyway, which then bounced on
  // submit with a confusing "Missing or expired reset token" inline
  // error. Show an explicit empty state when no token is in the URL.
  if (!token) {
    return (
      <main className="bg-background min-h-screen flex flex-col items-center justify-center p-margin-mobile md:p-margin-desktop">
        <section className="relative z-10 w-full max-w-[440px] bg-surface-container-lowest p-8 md:p-10 rounded-xl shadow-sm border border-outline-variant">
          <header className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-error-container text-on-error-container mb-4">
              <Icon name="link_off" className="text-3xl" />
            </div>
            <h1 className="font-headline-md text-headline-md text-on-surface mb-2">
              Invite or reset link missing
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              This page needs a token from the email link to set a password for
              your {displayName} account. The link may have expired or been
              truncated.
            </p>
          </header>
          <Link
            to="/forgot-password"
            className="block w-full text-center py-3.5 bg-primary text-on-primary font-label-md text-label-md rounded-lg hover:bg-opacity-90 transition-all"
          >
            Request a new reset link
          </Link>
          <footer className="mt-10 pt-6 border-t border-outline-variant text-center">
            <Link
              to="/sign-in"
              className="font-label-md text-label-md text-primary hover:underline"
            >
              Return to sign in
            </Link>
          </footer>
        </section>
      </main>
    );
  }

  return (
    <main className="bg-background min-h-screen flex flex-col items-center justify-center p-margin-mobile md:p-margin-desktop">
      <section className="relative z-10 w-full max-w-[440px] bg-surface-container-lowest p-8 md:p-10 rounded-xl shadow-sm border border-outline-variant">
        <header className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary mb-4">
            <Icon name="password" className="text-3xl" />
          </div>
          <h1 className="font-headline-md text-headline-md text-on-surface mb-2">
            Set a new password
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Choose a password for your {displayName} account. Minimum 12 characters.
          </p>
        </header>

        <form className="space-y-6" onSubmit={onSubmit} noValidate>
          <div className="space-y-2">
            <Label
              htmlFor="new-password"
              className="font-label-md text-label-md text-on-surface block px-1"
            >
              New password
            </Label>
            <div className="relative group">
              <Icon
                name="lock"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary transition-colors"
              />
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                className="pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="confirm-password"
              className="font-label-md text-label-md text-on-surface block px-1"
            >
              Confirm password
            </Label>
            <div className="relative group">
              <Icon
                name="lock"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-outline group-focus-within:text-primary transition-colors"
              />
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={submitting}
                className="pl-10"
              />
            </div>
          </div>

          {error ? (
            <p role="alert" className="font-body-md text-body-md text-error">
              {error}
            </p>
          ) : null}

          <Button type="submit" className="w-full" loading={submitting}>
            Set password and sign in
          </Button>
        </form>

        <footer className="mt-10 pt-6 border-t border-outline-variant text-center">
          <Link
            to="/sign-in"
            className="font-label-md text-label-md text-primary hover:underline"
          >
            Cancel and return to sign in
          </Link>
        </footer>
      </section>
    </main>
  );
}
